import { useCallback, useMemo, useState } from 'react';
import { ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../utils/utils.types';
import { ApexManager, getApexFinanceManager, getApexFinanceManagers } from '@round/api';
import { showNotification } from '../../../utils/showNotification';
import useAbortableEffect from '../../../hooks/useAbortableEffect';
import { SelectProps } from '@round/ui-kit';
import debounce from 'lodash/debounce';

const OPTIONS_PAGE_SIZE = 25;
type UseFinanceManagerSelectReturn = Pick<
    SelectProps<GenericDropdownOption<number>, false>,
    | 'isLoading'
    | 'noOptionsMessage'
    | 'value'
    | 'onChange'
    | 'options'
    | 'inputValue'
    | 'onInputChange'
    | 'onMenuScrollToBottom'
    | 'onMenuOpen'
    | 'onMenuClose'
>;

export default function useFinanceManagerSelect(initialManagerId?: number): UseFinanceManagerSelectReturn {
    const [isValueInitialized, setIsValueInitialized] = useState(false);
    const [value, setValue] = useState<ValueType<GenericDropdownOption<number>, false>>(null);

    const initValue = useCallback(async (initialManagerId: number, requestInit?: RequestInit) => {
        try {
            const response = await getApexFinanceManager(initialManagerId, requestInit);
            if (response.status === 404) {
                return;
            }

            setValue({ value: response.data.id, label: response.data.username });
            setIsValueInitialized(true);
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification('Could not initialize manager select', 'error');
            setIsValueInitialized(true);
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (!isValueInitialized && typeof initialManagerId === 'number') {
                initValue(initialManagerId, { signal });
            }
        },
        [initValue, initialManagerId, isValueInitialized]
    );

    const [page, setPage] = useState(1);
    const [managers, setManagers] = useState<ApexManager[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [areOptionsInitialized, setAreOptionsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [search, setSearch] = useState('');

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const fetchOptions = useCallback(
        async (params: Parameters<typeof getApexFinanceManagers>[0], requestInit?: RequestInit) => {
            try {
                setAreOptionsLoading(true);
                const response = await getApexFinanceManagers(params, requestInit);
                if (response.status === 404) {
                    return;
                }

                setManagers((managers) => managers.concat(response.data.results));
                setHasNextPage(Boolean(response.data.next));
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setError('Could not fetch managers');
            } finally {
                setAreOptionsLoading(false);
                setIsSearchLoading(false);
                setAreOptionsInitialized(true);
            }
        },
        []
    );

    const debouncedFetchOptions = useMemo(() => debounce(fetchOptions, 700), [fetchOptions]);

    const resetOptions = () => {
        setManagers([]);
        setHasNextPage(false);
        setAreOptionsLoading(false);
        setError(null);
        setAreOptionsInitialized(false);
    };

    useAbortableEffect(
        (signal) => {
            if (isMenuOpen && !areOptionsInitialized) {
                debouncedFetchOptions({ page, page_size: OPTIONS_PAGE_SIZE, search }, { signal });
            }
        },
        [areOptionsInitialized, debouncedFetchOptions, isMenuOpen, page, search]
    );

    return {
        options: useMemo(() => managers.map((m) => ({ value: m.id, label: m.username })), [managers]),
        value,
        onChange: setValue,
        inputValue: search,
        onInputChange: (value: string) => {
            if (search === value) {
                return;
            }

            resetOptions();
            setSearch(value);
            if (value) {
                setIsSearchLoading(true);
            }
        },
        isLoading: areOptionsLoading || isSearchLoading,
        noOptionsMessage: () => error ?? 'No options found',
        onMenuOpen: () => setIsMenuOpen(true),
        onMenuClose: () => setIsMenuOpen(false),
        onMenuScrollToBottom: () => {
            if (!hasNextPage || error) {
                return;
            }

            setPage((page) => page + 1);
        },
    };
}
